import { useState } from 'react'
import { Link } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { useBrand } from '@/brand/context'
import { formatDate, formatMonth } from '@/lib/format'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { downloadCsv, toCsv } from '@/lib/csv'
import { idempotencyKey } from '@/lib/idempotency'

type Invoice = Schema<'InvoiceOut'>
type Subscription = Schema<'SubscriptionOut'>

/**
 * How many invoices the card asks for, and the server's own ceiling
 * (`DEFAULT_INVOICES` and `MAX_INVOICES` in `api/app/v1/billing.py`).
 *
 * The route was a flat sixty rows with no cursor and no signal that there were
 * more (PLAT-9): about two and a half years of a monthly subscription plus its
 * overage invoices, after which an organisation's older tax documents left this
 * screen with nothing said. It now pages, and it says whether there is another
 * page.
 *
 * *Show more* raises the limit rather than following `next_before`, because the
 * ordering is `issued_at DESC` and stable — a larger limit is a superset of the
 * smaller one, so the list a customer is reading does not reflow under them.
 * `outreach.html#supp`'s suppression list made the same call for the same
 * reason. The cursor is what a client walking the whole history uses, and it is
 * what tells this card there is a history left to walk.
 */
const INVOICE_PAGE = 60
const MAX_INVOICES = 200

/**
 * The billing screen: `admin#invoices`, `admin#dunning` and `admin#cancel`.
 *
 * **The dunning panel has no card fields, and that is a deliberate departure
 * from the approved design.** `admin#dunning` draws card number, expiry and CVC
 * inputs in AugRank's own DOM. Collecting a PAN there would put AugRank inside
 * PCI DSS scope for no benefit, and it cannot work for the case that screen
 * exists for anyway: re-registering an Indian e-mandate happens on the bank's
 * page and nowhere else. So the button fetches the provider's hosted page. The
 * design file has been corrected to match rather than left disagreeing with
 * what ships.
 *
 * **Every date the tenant is shown comes from the API.** The grace cut-off, the
 * next retry, the period end: `app/billing/dunning.py` computes them from one
 * stored timestamp, so the screen states the exact date `admin#dunning`
 * promises rather than a vague "may be suspended" it worked out itself.
 *
 * **Cancellation is a screen of its own** — `/billing/cancel`, in
 * `CancelPage.tsx`. It was a `Modal` here, which is what ADM-18 and ADM-35…38
 * are all four about; the reasoning is in that file.
 */
export function BillingPage(): JSX.Element {
  const brand = useBrand()
  const { tenant, membership } = useActiveTenant()
  const toast = useToast()
  const manages = may(membership, 'billing:manage')
  const [gstin, setGstin] = useState<string | null>(null)
  const [address, setAddress] = useState<string | null>(null)

  const subscription = useQuery(
    (signal) => call(() => api.GET('/api/v1/billing/subscription', { signal })),
    [],
  )
  const [invoiceLimit, setInvoiceLimit] = useState(INVOICE_PAGE)
  const invoices = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/billing/invoices', {
          params: { query: { limit: invoiceLimit } },
          signal,
        }),
      ),
    [invoiceLimit],
  )
  const tax = useQuery(
    (signal) => call(() => api.GET('/api/v1/billing/tax-details', { signal })),
    [],
  )

  const resume = useMutation(() =>
    call(() =>
      api.DELETE('/api/v1/billing/subscription/cancellation', {
        params: { header: { 'Idempotency-Key': idempotencyKey('resume', tenant.id) } },
      }),
    ),
  )
  const paymentMethod = useMutation(() =>
    call(() => api.POST('/api/v1/billing/payment-method', {})),
  )
  const saveTax = useMutation((values: { gstin: string | null; billing_address: string | null }) =>
    call(() => api.PUT('/api/v1/billing/tax-details', { body: values })),
  )

  if (subscription.state.status === 'loading') return <Loading label="Loading billing" />
  if (subscription.state.status === 'error') {
    return <ErrorState failure={subscription.state.failure} onRetry={subscription.refetch} />
  }

  // `?? null` because the generated type is `SubscriptionOut | null |
  // undefined` — the field is optional in OpenAPI, so a `=== null` check
  // alone leaves `undefined` unnarrowed and every read below unsafe.
  const current = subscription.state.data.subscription ?? null

  // Bound out of the discriminated union once, because the export button reads
  // it inside a callback where TypeScript can no longer narrow `state` — and
  // because "which rows would be exported" should be one answer, not two.
  const invoiceRows = invoices.state.status === 'ready' ? invoices.state.data.invoices : []
  // Whether the server said there is an older page than the one held here. It
  // decides the export's *label*: "all" is a claim, and it is only true when
  // there is nothing left to page to.
  const moreInvoices = invoices.state.status === 'ready' && Boolean(invoices.state.data.next_before)

  async function openPaymentPage(): Promise<void> {
    const result = await paymentMethod.run(undefined)
    if (result.ok) window.location.assign(result.data.url)
  }

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row" style={{ justifyContent: 'space-between' }}>
        <h1 className="ar-h1">Billing</h1>
        <Link className="ar-btn ar-btn--ghost ar-btn--sm" to="/billing/plans">
          See all plans
        </Link>
      </div>

      {current === null && tenant.access === 'house' ? (
        <Empty
          /* 2026-09-12. One of AugmenTrait's own organisations, flagged by
             staff. There is no plan to show and none to choose: it is never
             billed, and offering *Choose a plan* here is how the company ends
             up paying itself through Razorpay to test its own product. */
          title="House account — nothing to pay"
          body="This is one of AugmenTrait's own organisations. It is never billed and never lapses, and scheduled runs, automatic follow-ups and LinkedIn discovery all run without a subscription. Staff set this from the admin console; the reason is in your audit log."
        />
      ) : current === null ? (
        <Empty
          /* AUTH-19. The trial no longer *starts* here — it started at signup —
             so this says what choosing a plan actually does.

             **Both halves used to be wrong for a lapsed organisation**, and it
             is the one that most needs the right words. `current === null` means
             *no subscription*, which is equally true of a trial that is running
             and of one that ended weeks ago, so somebody staring at a stopped
             product was told their trial was running. And "everything is
             included either way" stopped being true on 2026-09-10, when
             unattended work became the one thing a trial does not get. */
          title={tenant.access === 'lapsed' ? 'Your free trial has ended' : 'No subscription yet'}
          body={
            tenant.access === 'lapsed'
              ? 'The product is stopped until a plan is chosen. Nothing has been lost — your sites, leads and history are all still here.'
              : 'Your free trial is running. Choose a plan to carry on when it ends, and to turn on scheduled runs and automatic follow-ups — those wait for a subscription.'
          }
          action={
            manages ? (
              <Link className="ar-btn ar-btn--primary" to="/billing/plans">
                Choose a plan
              </Link>
            ) : undefined
          }
        />
      ) : (
        <>
          {current.status === 'past_due' && tenant.access !== 'house' ? (
            <Dunning
              subscription={current}
              manages={manages}
              pending={paymentMethod.pending}
              onUpdate={() => {
                void openPaymentPage()
              }}
            />
          ) : null}

          {tenant.access === 'house' ? (
            // Flagged after choosing a plan (the live tenant, 2026-09-19): the
            // row is real and Razorpay may hold a mandate on it, so the card
            // below stays — *Cancel subscription* is the way to end that — but
            // no warning about a trial ending or a card missing applies, because
            // nothing stops when it lapses.
            <div className="ar-alert ar-alert--info" role="status">
              <span className="ar-alert__icon" aria-hidden="true">
                ★
              </span>
              <div className="ar-alert__body">
                <strong>House account — nothing to pay</strong> This is one of AugmenTrait's own
                organisations. It is never billed and never lapses, whatever the plan below says;
                scheduled runs, automatic follow-ups and LinkedIn discovery all run without it. If
                the plan was chosen by mistake, cancel it so the payment provider holds nothing.
              </div>
            </div>
          ) : null}

          {current.status === 'trialing' &&
          current.payment_method === null &&
          tenant.access !== 'house' ? (
            // The live tenant on 2026-09-19: `trialing`, a Razorpay checkout
            // URL, `payment_method: null`, trial ending that afternoon — and a
            // card that read "Trial ends / Renews on" with nothing to say a
            // card might be missing. `payment_method` is filled only once a
            // charge has settled, so an authorised mandate also reads null
            // here; the sentence says so rather than accusing a customer who
            // did finish the page.
            <div className="ar-alert ar-alert--warning" role="status">
              <span className="ar-alert__icon" aria-hidden="true">
                ⚠
              </span>
              <div className="ar-alert__body">
                <strong>
                  No card on file yet — your trial ends{' '}
                  {(current.trial_ends_at ?? current.current_period_end) != null
                    ? `on ${formatDate(current.trial_ends_at ?? current.current_period_end ?? '')}`
                    : 'soon'}
                </strong>{' '}
                If you finished the payment page when you chose this plan, nothing more is needed:
                the first charge is taken when the trial ends. If you did not, add a card before
                then or the service stops.
                {manages ? (
                  <>
                    {' '}
                    <button
                      type="button"
                      className="ar-btn ar-btn--sm ar-btn--primary"
                      disabled={paymentMethod.pending}
                      onClick={() => {
                        void openPaymentPage()
                      }}
                    >
                      Add a card
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          ) : null}

          {current.cancel_at_period_end ? (
            <div className="ar-alert ar-alert--info">
              <span className="ar-alert__icon" aria-hidden="true">
                ◷
              </span>
              <div className="ar-alert__body">
                <strong>
                  Your subscription ends on{' '}
                  {current.cancellation_effective_at
                    ? formatDate(current.cancellation_effective_at)
                    : 'the end of this period'}
                </strong>{' '}
                You keep full access until then, and nothing further is charged.
              </div>
            </div>
          ) : null}

          <div className="ar-card">
            <div className="ar-card__header">
              <h2>{current.plan_name}</h2>
              <span className="ar-card__spacer" />
              <span className={`ar-badge ar-badge--${badgeTone(current.status)}`}>
                {statusLabel(current.status)}
              </span>
            </div>
            <div className="ar-card__body ar-stack">
              <div className="ar-row" style={{ justifyContent: 'space-between' }}>
                <span className="ar-muted">Price</span>
                <span className="ar-mono">{current.display} / month</span>
              </div>
              {current.trial_ends_at && current.status === 'trialing' ? (
                <div className="ar-row" style={{ justifyContent: 'space-between' }}>
                  <span className="ar-muted">Trial ends</span>
                  <span>{formatDate(current.trial_ends_at)}</span>
                </div>
              ) : null}
              {current.current_period_end ? (
                <div className="ar-row" style={{ justifyContent: 'space-between' }}>
                  <span className="ar-muted">
                    {/* A trial has never been charged, so its period end is the
                        first charge, not a renewal — "Trial ends 19 Sept ·
                        Renews on 19 Sept" was two labels for one date. */}
                    {current.cancel_at_period_end
                      ? 'Access until'
                      : current.status === 'trialing'
                        ? 'First charge on'
                        : 'Renews on'}
                  </span>
                  <span>{formatDate(current.current_period_end)}</span>
                </div>
              ) : null}
              {current.pending_plan_code ? (
                <div className="ar-row" style={{ justifyContent: 'space-between' }}>
                  <span className="ar-muted">Moving to</span>
                  <span>
                    {current.pending_plan_code}
                    {current.pending_effective_at
                      ? ` on ${formatDate(current.pending_effective_at)}`
                      : ''}
                  </span>
                </div>
              ) : null}
              {/* ADM-7 / NC-3. The method that will be debited, display-only:
                  brand and last four, which is the whole of what the provider
                  gives us and the whole of what a customer needs to recognise
                  which card is on file. `null` until a charge has settled. */}
              {current.payment_method ? (
                <div className="ar-row" style={{ justifyContent: 'space-between' }}>
                  <span className="ar-muted">Payment method</span>
                  <span>{current.payment_method.display}</span>
                </div>
              ) : null}
              {current.mandate.requires_afa ? (
                <p className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
                  This amount is above the ₹
                  {(current.mandate.ceiling_minor / 100).toLocaleString('en-IN')} e-mandate limit,
                  so your bank will ask you to approve each renewal with an OTP. We email you 24
                  hours before every charge.
                </p>
              ) : null}
            </div>
            {manages ? (
              <div className="ar-card__footer">
                {/* ADM-15. While past due, the one primary action on this screen
                    is on the failure card above, beside the message that
                    explains why it matters — so this ghost copy of it stands
                    down rather than offering the same thing twice, once
                    urgently and once not. */}
                {current.status === 'past_due' ? null : (
                  <Button
                    tone="ghost"
                    disabled={paymentMethod.pending}
                    onClick={() => {
                      void openPaymentPage()
                    }}
                  >
                    Update payment method
                  </Button>
                )}
                <span className="ar-card__spacer" />
                {current.cancel_at_period_end ? (
                  <Button
                    tone="primary"
                    disabled={resume.pending}
                    onClick={() => {
                      void resume.run(undefined).then((result) => {
                        if (result.ok) {
                          toast.show({ kind: 'success', title: 'Your subscription continues.' })
                          subscription.refetch()
                        }
                      })
                    }}
                  >
                    Keep my subscription
                  </Button>
                ) : (
                  // Quiet, not danger (parity P-INVC-05): the drawing reaches
                  // `admin#cancel` from elsewhere and never paints a red button
                  // on the invoices screen; the cancel page itself is where the
                  // weight belongs.
                  <Link className="ar-btn ar-btn--quiet" to="/billing/cancel">
                    Cancel subscription
                  </Link>
                )}
              </div>
            ) : null}
          </div>
          <InlineError failure={paymentMethod.state.failure ?? resume.state.failure} />
        </>
      )}

      <TaxDetails
        loading={tax.state.status === 'loading'}
        gstin={gstin ?? tax.state.data?.gstin ?? ''}
        address={address ?? tax.state.data?.billing_address ?? ''}
        editable={manages}
        pending={saveTax.pending}
        failure={saveTax.state.failure}
        onGstin={setGstin}
        onAddress={setAddress}
        onSave={() => {
          void saveTax
            .run({
              gstin: (gstin ?? tax.state.data?.gstin ?? '') || null,
              billing_address: (address ?? tax.state.data?.billing_address ?? '') || null,
            })
            .then((result) => {
              if (result.ok) {
                toast.show({ kind: 'success', title: 'Tax details saved.' })
                tax.refetch()
              }
            })
        }}
      />

      <div className="ar-card">
        <div className="ar-card__header">
          <h2>Invoices</h2>
          <span className="ar-card__spacer" />
          {/* ADM-11. The mockup's header button is "Download all (ZIP)" — a zip
              of invoice PDFs. **There are no PDFs.** On the INR path AugRank
              issues the invoice itself and stores it as rows, with no renderer
              anywhere in `api/`; on the USD path the document is Paddle's and
              lives at `external_url`. A button that produced an archive of
              nothing, or that fetched a customer's documents out of a vendor to
              re-serve them, would be a larger and worse thing than the row asks
              for.

              So the header action is the export that is *true*: every field this
              table holds, as a CSV an accountant can reconcile against, produced
              in the browser from data already fetched. The documents themselves
              stay one click away per row (ADM-12). Recorded as a departure. */}
          {invoiceRows.length > 0 ? (
            <Button
              size="sm"
              onClick={() => {
                downloadInvoices(invoiceRows, tenant.slug)
              }}
            >
              {/* It exports the rows the table is showing, so it says "all" only
                  when those are all of them. With an older page still to fetch,
                  a button labelled "Download all" hands sixty of two hundred
                  invoices to somebody reconciling a year — and nothing about the
                  file would tell them it was short. */}
              {moreInvoices ? 'Download these (CSV)' : 'Download all (CSV)'}
            </Button>
          ) : null}
        </div>
        {invoices.state.status === 'loading' ? (
          <div className="ar-card__body">
            <Loading label="Loading invoices" />
          </div>
        ) : invoices.state.status === 'error' ? (
          <div className="ar-card__body">
            <ErrorState failure={invoices.state.failure} onRetry={invoices.refetch} />
          </div>
        ) : invoices.state.data.invoices.length === 0 ? (
          <div className="ar-card__body">
            <Empty
              title="No invoices yet"
              body="Your first invoice appears here once a payment clears."
            />
          </div>
        ) : (
          <>
            <div className="ar-table-wrap" tabIndex={0}>
              <table className="ar-table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Period</th>
                    <th className="ar-num">Amount</th>
                    <th>Status</th>
                    {/* ADM-31. The fifth column the design draws, for the
                        per-row document action. */}
                    <th>
                      <span className="ar-visually-hidden">Document</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.state.data.invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td className="ar-mono">{invoice.number}</td>
                      {/* ADM-13. The month, not the day it was raised: the period
                          runs to the end of it, and "1 Jul 2026" reads as an
                          invoice *for* that day. */}
                      <td>{formatMonth(invoice.period_start)}</td>
                      <td className="ar-num">{invoice.total_display}</td>
                      <td>
                        <span className={`ar-badge ar-badge--${invoiceTone(invoice.kind)}`}>
                          {invoice.kind === 'trial' ? 'Trial' : statusLabel(invoice.status)}
                        </span>
                      </td>
                      {/* ADM-12. A visible per-row control, and an explanation
                          where there is nothing to give. The number used to
                          double as the link when `external_url` was set and be
                          plain text when it was not — which meant the trial row
                          silently offered nothing and looked like a rendering
                          bug. Trial records are not tax invoices (nothing was
                          supplied for consideration, and `app/billing/
                          invoices.py` refuses to raise one), so the honest
                          answer is to say so. */}
                      <td className="ar-num">
                        {invoice.external_url ? (
                          <a
                            className="ar-btn ar-btn--quiet ar-btn--sm"
                            href={invoice.external_url}
                            rel="noreferrer"
                            target="_blank"
                          >
                            Receipt
                          </a>
                        ) : (
                          <span className="ar-subtle ar-text-body-sm">
                            {invoice.kind === 'trial' ? 'No charge' : 'On request'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {invoices.state.data.next_before ? (
              <div className="ar-card__body">
                {invoiceLimit < MAX_INVOICES ? (
                  <div className="ar-row">
                    <span className="ar-subtle ar-text-body-sm">
                      Showing your {invoices.state.data.invoices.length} most recent.
                    </span>
                    <span className="app-spacer" />
                    <Button
                      size="sm"
                      onClick={() => {
                        setInvoiceLimit(MAX_INVOICES)
                      }}
                    >
                      Show more
                    </Button>
                  </div>
                ) : (
                  <span className="ar-subtle ar-text-body-sm">
                    Showing your {MAX_INVOICES} most recent, which is as many as {brand.productName}{' '}
                    will send at once. Ask support for anything older and we will send it to you.
                  </span>
                )}
              </div>
            ) : null}
            <div className="ar-card__footer">
              <Seller seller={invoices.state.data.seller} />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

/**
 * `admin#dunning` — the failed-payment screen (ADM-14, ADM-15, ADM-33, ADM-34).
 *
 * **It was one alert.** The design draws an alert *and* a card with two panels:
 * one that says nothing has stopped and when it will, and one that explains what
 * pressing the button actually does. Both exist for the same reason the screen
 * exists at all — a failed card is usually an expiry, not a decision to leave —
 * and a tenant who is told only that a charge failed has been given the alarm
 * without the reassurance.
 *
 * **The card is 38rem and centred** (ADM-33), which is what makes it read as the
 * one thing on the page that needs answering, and **its footer carries the
 * primary** (ADM-34). It used to be a ghost button in the subscription card
 * below, in every status, so the one moment it mattered it was the quietest
 * control on the screen.
 *
 * **What is not here is the 90-day retention promise** (NC-4). The mockup's
 * first panel ends "your data and rankings are kept for 90 days regardless" —
 * nothing in this product states such a window, and a screen is the wrong place
 * to invent a commitment the erasure runner has never been told about.
 *
 * Every date comes off the API, computed by `app/billing/dunning.py` from one
 * stored timestamp. The two retry dates the mockup shows are one field here:
 * `next_retry_at` is the next one, and naming a second the provider has not
 * scheduled would be a date AugRank made up.
 */
function Dunning({
  subscription,
  manages,
  pending,
  onUpdate,
}: {
  subscription: Subscription
  manages: boolean
  pending: boolean
  onUpdate: () => void
}): JSX.Element {
  const brand = useBrand()
  return (
    <div className="ar-stack" style={{ maxWidth: '38rem', margin: '0 auto', width: '100%' }}>
      <div className="ar-alert ar-alert--danger">
        <span className="ar-alert__icon" aria-hidden="true">
          ✕
        </span>
        <div className="ar-alert__body">
          <strong>We couldn&apos;t charge your payment method</strong>
          {subscription.payment_method ? ` — ${subscription.payment_method.display}` : ''}
          {subscription.last_failure_message ? ` — ${subscription.last_failure_message}.` : '.'}
        </div>
      </div>
      <div className="ar-card">
        <div className="ar-card__body ar-stack">
          <div className="ar-panel">
            <strong>Nothing has stopped yet</strong>
            <p
              className="ar-muted"
              style={{
                margin: 'var(--ar-space-2) 0 0',
                fontSize: 'var(--ar-text-body-sm-size)',
              }}
            >
              {subscription.next_retry_at
                ? `We will try again on ${formatDate(subscription.next_retry_at)}. `
                : ''}
              Your work carries on as normal until{' '}
              <strong>
                {subscription.grace_ends_at ? formatDate(subscription.grace_ends_at) : 'soon'}
              </strong>
              . After that, runs pause — nothing is deleted.
            </p>
          </div>
          <div className="ar-panel">
            <strong>Update your payment method</strong>
            <p
              className="ar-muted"
              style={{
                margin: 'var(--ar-space-2) 0 0',
                fontSize: 'var(--ar-text-body-sm-size)',
              }}
            >
              We send you to {subscription.provider === 'paddle' ? 'Paddle' : 'Razorpay'}&apos;s own
              page to add a card or re-approve your mandate.{' '}
              <strong>Card details never reach {brand.productName}</strong> — we do not have them
              and cannot ask for them. It takes about a minute, and the retry happens as soon as the
              new method is authorised.
            </p>
          </div>
        </div>
        {manages ? (
          <div className="ar-card__footer">
            <span className="ar-card__spacer" />
            <Button tone="primary" disabled={pending} busy={pending} onClick={onUpdate}>
              Update payment method →
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

/**
 * The invoice list as a CSV (ADM-11).
 *
 * One row per invoice with the fields a reconciliation needs — the number, the
 * period, the tax split, and the link to the provider's own document where there
 * is one. Amounts are written in **major units as plain numbers**, not through
 * `total_display`: the display strings carry ₹ and Indian digit grouping, and a
 * comma inside a cell in a comma-separated file is exactly the bug
 * `lib/csv.toCsv` exists to prevent — it would quote it correctly and the reader
 * would still get text where it wanted a number.
 */
function downloadInvoices(rows: Invoice[], slug: string): void {
  const csv = toCsv(
    [
      'Invoice',
      'Period start',
      'Period end',
      'Issued',
      'Kind',
      'Status',
      'Currency',
      'Subtotal',
      'CGST',
      'SGST',
      'IGST',
      'Total',
      'Document',
    ],
    rows.map((invoice) => [
      invoice.number,
      invoice.period_start,
      invoice.period_end,
      invoice.issued_at,
      invoice.kind,
      invoice.status,
      invoice.currency,
      invoice.subtotal_minor / 100,
      invoice.cgst_minor / 100,
      invoice.sgst_minor / 100,
      invoice.igst_minor / 100,
      invoice.total_minor / 100,
      invoice.external_url ?? '',
    ]),
  )
  downloadCsv(`${slug}-invoices.csv`, csv)
}

/**
 * The organisation's own GSTIN and billing address — S-D3's Organisation panel
 * fields, rendered here because this is where they are used.
 *
 * The GSTIN decides CGST + SGST against IGST on every invoice, and the server
 * checks its check character: a wrong one produces a valid-looking document
 * claiming the wrong head, which is the recipient's problem to unpick with
 * their own tax authority.
 */
function TaxDetails({
  loading,
  gstin,
  address,
  editable,
  pending,
  failure,
  onGstin,
  onAddress,
  onSave,
}: {
  loading: boolean
  gstin: string
  address: string
  editable: boolean
  pending: boolean
  failure: Parameters<typeof InlineError>[0]['failure']
  onGstin: (value: string) => void
  onAddress: (value: string) => void
  onSave: () => void
}): JSX.Element {
  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h2>Your tax details</h2>
      </div>
      <div className="ar-card__body ar-stack">
        {loading ? (
          <Loading label="Loading tax details" />
        ) : (
          <>
            <Field
              label="GSTIN"
              help="Optional. With it, your invoices carry the right tax head for input credit."
            >
              {(props) => (
                <input
                  {...props}
                  className="ar-input ar-mono"
                  value={gstin}
                  disabled={!editable}
                  placeholder="36ABFCA6453L1ZC"
                  onChange={(event) => {
                    onGstin(event.target.value)
                  }}
                />
              )}
            </Field>
            <Field label="Billing address">
              {(props) => (
                <textarea
                  id={props.id}
                  aria-describedby={props['aria-describedby']}
                  className="ar-input"
                  rows={3}
                  value={address}
                  disabled={!editable}
                  onChange={(event) => {
                    onAddress(event.target.value)
                  }}
                />
              )}
            </Field>
            <InlineError failure={failure} />
          </>
        )}
      </div>
      {editable && !loading ? (
        <div className="ar-card__footer">
          <span className="ar-card__spacer" />
          <Button tone="primary" disabled={pending} onClick={onSave}>
            Save
          </Button>
        </div>
      ) : null}
    </div>
  )
}

/**
 * The supplier block CGST Rule 46 requires on the face of a tax invoice.
 *
 * Rendered from what the API sends rather than from constants here: the legal
 * name, the registered address and the GSTIN are a legal statement that can
 * change — an address moves, a registration is re-issued — and a copy in the
 * SPA would be a second version of a document nobody would think to update.
 */
function Seller({
  seller,
}: {
  seller: {
    legal_name: string
    gstin: string
    sac_code: string
    address_lines: string[]
    cin: string
  }
}): JSX.Element {
  return (
    <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
      Issued by {seller.legal_name}, {seller.address_lines.join(', ')} · GSTIN {seller.gstin} · CIN{' '}
      {seller.cin} · SAC {seller.sac_code} · valid for input credit. This is the{' '}
      <em>seller&apos;s</em> GSTIN — AugmenTrait&apos;s own. The one on Settings → Organisation is
      the tenant&apos;s, and the two are never the same number.
    </span>
  )
}

function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    trialing: 'Trial',
    active: 'Active',
    past_due: 'Payment failed',
    cancelled: 'Cancelled',
    expired: 'Ended',
    paid: '✓ Paid',
    open: 'Due',
    void: 'Void',
    refunded: 'Refunded',
  }
  return labels[status] ?? status
}

function badgeTone(status: string): string {
  if (status === 'past_due') return 'danger'
  if (status === 'trialing') return 'brand'
  if (status === 'active') return 'success'
  return 'neutral'
}

function invoiceTone(kind: string): string {
  return kind === 'trial' ? 'neutral' : 'success'
}
